import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useProfileSetup } from "../../../context/ProfileSetupContext";
import {
  ProfileSetupCompleteActions,
  ProfileSetupCompleteSummary,
  ProfileSetupContinueLater,
  ProfileSetupProgressList,
} from "./ProfileSetupProgressContent";

export default function SetupProgressStep({ embedded = false, onComplete = null }) {
  const { steps, isComplete, isLoading, hasActiveSteps, error, opportunityCount, startSetup } =
    useProfileSetup();

  const handleComplete = onComplete ?? (() => {});

  useEffect(() => {
    startSetup();
  }, [startSetup]);

  if (error) {
    return (
      <div
        className={`flex flex-col items-center justify-center px-2 py-8 text-center ${
          embedded ? "min-h-0" : "min-h-[40vh]"
        }`}
      >
        <p className="mb-2 text-lg font-semibold text-slate-900">Setup interrupted</p>
        <p className="mb-0 max-w-md text-sm text-slate-600">{error}</p>
        <div className="mt-6">
          <ProfileSetupContinueLater />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col items-center justify-center px-2 py-6 ${
        embedded ? "min-h-0 md:py-6" : "min-h-[50vh] md:py-10"
      }`}
    >
      <AnimatePresence mode="wait">
        {!isComplete ? (
          <motion.div
            key="progress"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="w-full max-w-md"
          >
            <div className="mb-8 text-center">
              <h2 className="mb-2 text-xl font-bold text-slate-900 md:text-2xl">
                Setting up your profile
              </h2>
              <p className="mb-0 text-sm text-slate-600 md:text-base">
                {isLoading && !hasActiveSteps
                  ? "Starting trend discovery…"
                  : "We're finding marketing trends tailored to your business."}
              </p>
            </div>

            <ProfileSetupProgressList
              steps={steps}
              isLoading={isLoading}
              hasActiveSteps={hasActiveSteps}
            />
            <ProfileSetupContinueLater />
          </motion.div>
        ) : (
          <motion.div
            key="complete"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-lg text-center"
          >
            <ProfileSetupCompleteSummary opportunityCount={opportunityCount} />
            <ProfileSetupCompleteActions
              opportunityCount={opportunityCount}
              compact={embedded}
              onExplore={onComplete ? handleComplete : null}
              onCreateCampaign={onComplete ? handleComplete : null}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
